import { useFormik } from "formik";
import * as Yup from "yup";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useState, useEffect, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { ImageTemplateDemo } from "../images/file-upload-template";
import { MenuType } from "../../types/enum";
import { usePageNavigation } from "../user-menu/menu/navigation";

const CustomerServComplaintRegister: React.FC = () => {
  const [imageFile, setImageFile] = useState<any>(null);
  const toast = useRef<Toast>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loginUser, setLoginUser] = useState<any>(null);
  const navigate = useNavigate();
  const [containerType, setContainerType] = useState<string>("Complaints");
  const navigateToPage = usePageNavigation();
  const initialValues = {
    description: "",
    image: null as string | null,
    video: null as string | null,
    severity: null as string | null,
    remarks: null as string | null,
    customerSupportAction: null as string | null,
  };

  const validationSchema = Yup.object({
    description: Yup.string().required("Description is required"),
  });

  useEffect(() => {
    const LoginDetails = localStorage.getItem('user');
    if (LoginDetails) {
      const user = JSON.parse(LoginDetails);
      setLoginUser(user);
    }
  }, []);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      if (isSubmitted) return;
      setIsSubmitted(true);
      const complaint = {
        CustomerId: loginUser?.userID || 0,
        Description: data.description,
        Image: data.image,
        Severity: data.severity,
        Remarks: data.remarks,
        CustomerSupportAction: data.customerSupportAction,
        CreatedBy: loginUser?.userID || 0,
        CreatedDate: new Date().toISOString(),
        ContainerType: containerType
      };

      try {
        const response = await ComplaintService.CreateCustomerServiceComplaint(complaint);

        if (response.isSuccess) {
          showMessage('Success', 'Service complaint created successfully', 'success');
          navigateToPage(MenuType.ViewAllComplaints, { state: { complaintType: 'service' } });
          formik.resetForm();
          setImageFile(null);
        } else {
          showMessage('Error', response.message, 'error');
        }
      } catch (error) {
        console.error("Error during service complaint submission", error);
        showMessage('Error', 'Failed to create service complaint', 'error');
      } finally {
        setIsSubmitted(false);
      }
    },
  });

  const showMessage = (label: string, message: string, severity: 'success' | 'error' | 'info' | 'warn') => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const renderDescriptionField = () => (
    <div className="field col-12 mb-3" style={{ paddingLeft: 0, paddingRight: 0 }}>
      <label htmlFor="description" style={{ fontWeight: 'bold', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', marginBottom: '0.5rem', display: 'block' }}>
        Description<span style={{ color: "red" }}>*</span>
      </label>
      <InputTextarea
        placeholder="Enter Description"
        name="description"
        id="description"
        rows={5}
        className="w-full"
        value={formik.values.description}
        onChange={(e) => {
          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
          formik.setFieldValue('description', cleanedValue);
        }}
        style={{
          border: "1px solid #d0d0d0",
          padding: '0.75rem',
          borderRadius: '4px',
          fontFamily: 'inherit',
          fontSize: 'clamp(0.9rem, 1.5vw, 1rem)'
        }}
      />
      {formik.errors.description && formik.touched.description && (
        <small className="p-error">{formik.errors.description as string}</small>
      )}
    </div>
  );

  const renderButtons = () => (
    <div className="flex justify-content-center sm:justify-content-center lg:justify-content-end gap-2 sm:gap-3 lg:gap-4 mt-4 mb-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
      <Button
        type="button"
        label="Reset"
        className="p-button-secondary"
        style={{ padding: 'clamp(0.5rem, 1vw, 0.75rem) clamp(1rem, 2vw, 1.5rem)', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}
        onClick={() => formik.resetForm()}
      />
      <Button
        type="submit"
        label="Raise Complaint"
        className="p-button-primary"
        style={{ padding: 'clamp(0.5rem, 1vw, 0.75rem) clamp(1rem, 2vw, 1.5rem)', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}
        disabled={isSubmitted}
      />
    </div>
  );

  const handleFileUpload = (file: any) => {
    setImageFile(file);
    formik.setFieldValue("image", file);
  };

  const handleFileSizeExceeded = (message: string) => {
    showMessage('Info', message, 'info');
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div style={{
        padding: 'clamp(0.75rem, 3vw, 2rem)',
        backgroundColor: '#f9f9f9',
        minHeight: '100vh'
      }}>
        <h2 style={{
          color: "green",
          textAlign: 'center',
          marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)',
          marginTop: 0,
          fontSize: 'clamp(0.5rem, 2vw, 4.5rem)',
          //fontWeight: 'bold'
        }}>
          Service Complaint Register
        </h2>

        <div style={{
          maxWidth: '1200px',
          margin: '0rem 5rem 0rem 5rem',
          border: '2px solid #999999',
          borderRadius: 'clamp(0.25rem, 1vw, 0.75rem)',
          backgroundColor: '#ffffff',
          padding: 'clamp(1rem, 3vw, 2rem)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            <div className="grid" style={{ gap: 'clamp(0.5rem, 1vw, 1rem)' }}>
              {renderDescriptionField()}
              <div className="field col-12 px-0 mb-3">
                <ImageTemplateDemo onFileUpload={handleFileUpload} handelFileSize={handleFileSizeExceeded} />
              </div>
            </div>
            {renderButtons()}
          </form>
        </div>
      </div>
    </>
  );
};

export default CustomerServComplaintRegister;